import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, RefreshCw, Trash2, Target, Award, AlertCircle,
  TrendingUp, TrendingDown, Clock, CheckCircle, XCircle,
  Calendar, ChevronRight, Edit2, PauseCircle
} from 'lucide-react';
import api from '../utils/api';
import { formatCurrency } from '../utils/format';
import { useTenant } from '../contexts/TenantContext';
import useElasticHover from '../hooks/useElasticHover';

/* ─── constants ─────────────────────────────────────────── */
const TIPO_META_OPTIONS = [
  { value: 'RECEITA',  label: 'Meta de Receita',   desc: 'Quero receber X no período',  color: '#22c55e' },
  { value: 'DESPESA',  label: 'Limite de Despesa',  desc: 'Não gastar mais que X no período',  color: '#ef4444' },
  { value: 'CUSTO',    label: 'Limite de Custo',    desc: 'Controlar custos até X',  color: '#f59e0b' },
  { value: 'ECONOMIA', label: 'Meta de Economia',   desc: 'Sobrar X (receitas − saídas)',  color: '#3b82f6' },
  { value: 'SALDO',    label: 'Saldo Acumulado',    desc: 'Quero ter saldo total de X',  color: '#8b5cf6' },
];

const PRAZO_MAP = {
  curto: { label: 'Curto prazo',  desc: 'até 3 meses',  color: '#ef4444', bg: '#fef2f2' },
  medio: { label: 'Médio prazo',  desc: '3 a 12 meses', color: '#f59e0b', bg: '#fffbeb' },
  longo: { label: 'Longo prazo',  desc: 'mais de 1 ano',color: '#8b5cf6', bg: '#f5f3ff' },
};

const STATUS_TABS = [
  { id: 'todas',     label: 'Todas'       },
  { id: 'ativa',     label: 'Em andamento'},
  { id: 'concluida', label: 'Concluídas'  },
  { id: 'atrasada',  label: 'Atrasadas'   },
];

function tipoMeta(v) {
  return TIPO_META_OPTIONS.find(t => t.value === v) || TIPO_META_OPTIONS[0];
}

function prazoInfo(v) {
  return PRAZO_MAP[v] || PRAZO_MAP['medio'];
}

/* ─── progress bar ───────────────────────────────────────── */
function ProgressBar({ pct, color }) {
  return (
    <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${Math.min(pct, 100)}%`, background: color }}
      />
    </div>
  );
}

/* ─── meta card ──────────────────────────────────────────── */
function MetaCard({ meta, onEdit, onDelete, onToggleStatus, accent }) {
  const tipo   = tipoMeta(meta.tipoMeta || meta.tipo);
  const prazo  = prazoInfo(meta.prazo || meta.prazoCalc);
  const pct    = meta.percentual || 0;
  const atingida = pct >= 100;
  const atrasada = meta.atrasada;
  const cancelada = meta.statusMeta === 'cancelada';

  const borderColor = cancelada ? '#e2e8f0'
    : atrasada ? '#fecaca'
    : atingida ? '#bbf7d0'
    : '#e2e8f0';

  const fmtDate = (s) => {
    if (!s) return '—';
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <div
      className={`fin-card p-5 flex flex-col gap-4 ${cancelada ? 'opacity-60' : ''}`}
      style={{ border: `1.5px solid ${borderColor}` }}
    >
      {/* top row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="min-w-0">
            <p className="font-bold text-slate-800 dark:text-slate-100 text-sm leading-tight truncate">
              {meta.titulo || meta.descricao}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span
                className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                style={{ color: tipo.color, background: `${tipo.color}18` }}
              >
                {tipo.label}
              </span>
              <span
                className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                style={{ color: prazo.color, background: prazo.bg }}
              >
                {prazo.label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {!cancelada && (
            <button
              onClick={() => onEdit(meta)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              title="Editar"
            >
              <Edit2 size={13} />
            </button>
          )}
          <button
            onClick={() => onDelete(meta.id)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            title="Remover"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* description */}
      {meta.descricaoDetalhada && (
        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed -mt-1">
          {meta.descricaoDetalhada}
        </p>
      )}

      {/* period */}
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <Calendar size={11} />
        <span>{fmtDate(meta.dataInicio)}</span>
        <ChevronRight size={10} />
        <span className="font-semibold text-slate-600 dark:text-slate-300">{fmtDate(meta.dataFim)}</span>
        {meta.diasRestantes !== undefined && !atingida && !cancelada && (
          <span className={`ml-auto font-bold ${atrasada ? 'text-red-500' : meta.diasRestantes <= 30 ? 'text-amber-500' : 'text-slate-400'}`}>
            {atrasada ? `${Math.abs(meta.diasRestantes)}d atrasada` : `${meta.diasRestantes}d restantes`}
          </span>
        )}
        {atingida && <span className="ml-auto font-bold text-emerald-600">✓ Atingida!</span>}
      </div>

      {/* progress */}
      <div>
        <div className="flex justify-between items-end mb-1.5">
          <div>
            <p className="text-xs text-slate-400 font-medium">Realizado</p>
            <p className="text-base font-bold" style={{ color: tipo.color }}>
              {formatCurrency(meta.progresso || meta.realizado || 0)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400 font-medium">Meta</p>
            <p className="text-base font-bold text-slate-700 dark:text-slate-200">
              {formatCurrency(meta.valorMeta)}
            </p>
          </div>
        </div>
        <ProgressBar pct={pct} color={atingida ? '#22c55e' : tipo.color} />
        <p className="text-xs font-bold mt-1" style={{ color: atingida ? '#22c55e' : tipo.color }}>
          {pct.toFixed(1)}%
          {!atingida && meta.valorMeta - (meta.progresso || 0) > 0 && (
            <span className="text-slate-400 font-normal ml-1">
              · faltam {formatCurrency(meta.valorMeta - (meta.progresso || 0))}
            </span>
          )}
        </p>
      </div>

      {/* footer actions */}
      {!cancelada && (
        <div className="flex gap-2 pt-1 border-t border-slate-50 dark:border-slate-700">
          {atingida && meta.statusMeta !== 'concluida' && (
            <button
              onClick={() => onToggleStatus(meta.id, 'concluida')}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-1.5 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
            >
              <CheckCircle size={12} /> Marcar concluída
            </button>
          )}
          {meta.statusMeta === 'ativa' && (
            <button
              onClick={() => onToggleStatus(meta.id, 'cancelada')}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-400 hover:bg-slate-100 transition-colors ml-auto"
            >
              <PauseCircle size={11} /> Cancelar
            </button>
          )}
        </div>
      )}
      {cancelada && (
        <div className="flex gap-2 pt-1 border-t border-slate-50 dark:border-slate-700">
          <button
            onClick={() => onToggleStatus(meta.id, 'ativa')}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-1.5 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
          >
            <RefreshCw size={11} /> Reativar
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── form modal ─────────────────────────────────────────── */
const today = new Date().toISOString().split('T')[0];
const endOfYear = `${new Date().getFullYear()}-12-31`;

const initialForm = {
  titulo: '',
  descricaoDetalhada: '',
  tipoMeta: 'RECEITA',
  valorMeta: '',
  dataInicio: today,
  dataFim: endOfYear,
};

function calcPrazoLabel(dataFim) {
  if (!dataFim) return null;
  const dias = Math.round((new Date(dataFim) - new Date()) / 86400000);
  if (dias < 0) return null;
  if (dias <= 90)  return { ...PRAZO_MAP.curto, key: 'curto' };
  if (dias <= 365) return { ...PRAZO_MAP.medio, key: 'medio' };
  return { ...PRAZO_MAP.longo, key: 'longo' };
}

function MetaModal({ open, onClose, onSaved, editItem, accent }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(editItem ? {
        titulo: editItem.titulo || editItem.descricao || '',
        descricaoDetalhada: editItem.descricaoDetalhada || '',
        tipoMeta: editItem.tipoMeta || editItem.tipo || 'RECEITA',
        valorMeta: editItem.valorMeta?.toString() || '',
        dataInicio: editItem.dataInicio || today,
        dataFim: editItem.dataFim || endOfYear,
      } : initialForm);
      setError('');
    }
  }, [open, editItem]);

  if (!open) return null;

  const prazoLabel = calcPrazoLabel(form.dataFim);
  const tipoSel = tipoMeta(form.tipoMeta);

  const handleSave = async () => {
    if (!form.titulo.trim()) { setError('Título é obrigatório.'); return; }
    const val = parseFloat(form.valorMeta.replace(',', '.'));
    if (!val || val <= 0) { setError('Valor da meta inválido.'); return; }
    if (!form.dataFim) { setError('Data final obrigatória.'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = { ...form, valorMeta: val };
      if (editItem) {
        await api.put(`/metas/${editItem.id}`, payload);
      } else {
        await api.post('/metas', payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 nexus-modal-overlay">
      <div className="nexus-modal w-full max-w-lg max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <h2 className="font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {editItem ? 'Editar Meta' : 'Nova Meta'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {/* Título */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Título da meta *</label>
            <input
              className="fin-input w-full"
              placeholder="Ex: Acumular R$ 100 mil até dezembro"
              value={form.titulo}
              onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
            />
          </div>

          {/* Descrição detalhada */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Descrição <span className="font-normal normal-case">(opcional)</span>
            </label>
            <textarea
              className="fin-input w-full resize-none"
              rows={3}
              placeholder="Descreva sua meta com mais detalhes, estratégia, motivação..."
              value={form.descricaoDetalhada}
              onChange={e => setForm(f => ({ ...f, descricaoDetalhada: e.target.value }))}
            />
          </div>

          {/* Tipo da meta */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Tipo da meta *
            </label>
            <div className="grid grid-cols-1 gap-2">
              {TIPO_META_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    form.tipoMeta === opt.value
                      ? 'border-current shadow-sm'
                      : 'border-slate-200 dark:border-slate-600 hover:border-slate-300'
                  }`}
                  style={form.tipoMeta === opt.value ? { borderColor: opt.color, background: `${opt.color}08` } : {}}
                >
                  <input
                    type="radio"
                    className="hidden"
                    value={opt.value}
                    checked={form.tipoMeta === opt.value}
                    onChange={() => setForm(f => ({ ...f, tipoMeta: opt.value }))}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{opt.label}</p>
                    <p className="text-xs text-slate-400">{opt.desc}</p>
                  </div>
                  {form.tipoMeta === opt.value && (
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: opt.color }}>
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Valor */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Valor alvo (R$) *
            </label>
            <input
              className="fin-input w-full text-lg font-bold"
              placeholder="0,00"
              value={form.valorMeta}
              onChange={e => setForm(f => ({ ...f, valorMeta: e.target.value }))}
            />
          </div>

          {/* Período */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Período da meta
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-slate-400 mb-1">De</p>
                <input
                  type="date"
                  className="fin-input w-full"
                  value={form.dataInicio}
                  onChange={e => setForm(f => ({ ...f, dataInicio: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Até (prazo final) *</p>
                <input
                  type="date"
                  className="fin-input w-full"
                  value={form.dataFim}
                  onChange={e => setForm(f => ({ ...f, dataFim: e.target.value }))}
                />
              </div>
            </div>
            {prazoLabel && (
              <div
                className="flex items-center gap-2 mt-2 px-3 py-2 rounded-xl text-xs font-semibold"
                style={{ color: prazoLabel.color, background: prazoLabel.bg }}
              >
                <Clock size={12} />
                {prazoLabel.label} · {prazoLabel.desc}
                {(() => {
                  const dias = Math.round((new Date(form.dataFim) - new Date()) / 86400000);
                  return ` · ${dias} dias restantes`;
                })()}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose} className="fin-btn-secondary">Cancelar</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="fin-btn-primary"
            style={{ background: accent }}
          >
            {saving ? 'Salvando...' : editItem ? 'Salvar alterações' : 'Criar Meta'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── summary mini-dashboard ─────────────────────────────── */
function SummaryDashboard({ metas, accent }) {
  const ativas    = metas.filter(m => m.statusMeta === 'ativa');
  const atrasadas = metas.filter(m => m.atrasada && m.statusMeta === 'ativa');
  const concluidas = metas.filter(m => m.statusMeta === 'concluida' || m.percentual >= 100);
  const totalMeta = ativas.reduce((s, m) => s + (m.valorMeta || 0), 0);
  const totalReal = ativas.reduce((s, m) => s + (m.progresso || 0), 0);
  const avgPct = ativas.length > 0 ? ativas.reduce((s, m) => s + (m.percentual || 0), 0) / ativas.length : 0;

  const stats = [
    { label: 'Metas ativas',    value: ativas.length,    icon: Target,       color: accent,     fmt: 'num' },
    { label: 'Total das metas', value: totalMeta,         icon: TrendingUp,   color: '#22c55e',  fmt: 'cur' },
    { label: 'Realizado',       value: totalReal,         icon: TrendingUp,   color: '#3b82f6',  fmt: 'cur' },
    { label: 'Concluídas',      value: concluidas.length, icon: Award,        color: '#8b5cf6',  fmt: 'num' },
    { label: 'Atrasadas',       value: atrasadas.length,  icon: AlertCircle,  color: atrasadas.length > 0 ? '#ef4444' : '#94a3b8', fmt: 'num' },
    { label: 'Progresso médio', value: avgPct,            icon: Target,       color: accent,     fmt: 'pct' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {stats.map(s => <StatCard key={s.label} {...s} />)}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, fmt }) {
  const elasticRef = useElasticHover();
  return (
    <div
      ref={elasticRef}
      className="fin-card hover:shadow-lg transition-shadow duration-300 p-4 flex flex-col justify-between aspect-square min-h-[130px]"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider leading-tight">{label}</p>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
          <Icon size={15} style={{ color }} />
        </div>
      </div>
      <p className="text-xl font-bold leading-tight truncate" style={{ color, fontFamily: 'Manrope, sans-serif' }}>
        {fmt === 'cur' ? formatCurrency(value)
          : fmt === 'pct' ? `${value.toFixed(1)}%`
          : value}
      </p>
    </div>
  );
}

/* ─── main page ──────────────────────────────────────────── */
export default function Metas() {
  const { tenant } = useTenant();
  const accent = tenant?.corPrimaria || '#0147a0';

  const [metas, setMetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [tab, setTab] = useState('todas');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/metas/progresso');
      setMetas(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta meta?')) return;
    try {
      await api.delete(`/metas/${id}`);
      setMetas(prev => prev.filter(m => m.id !== id));
    } catch (err) { console.error(err); }
  };

  const handleToggleStatus = async (id, newStatus) => {
    try {
      await api.patch(`/metas/${id}/status`, { statusMeta: newStatus });
      setMetas(prev => prev.map(m => m.id === id ? { ...m, statusMeta: newStatus } : m));
    } catch (err) { console.error(err); }
  };

  // filter by tab
  const displayed = tab === 'todas'     ? metas
    : tab === 'ativa'     ? metas.filter(m => m.statusMeta === 'ativa' && !m.atrasada)
    : tab === 'concluida' ? metas.filter(m => m.statusMeta === 'concluida' || m.percentual >= 100)
    : tab === 'atrasada'  ? metas.filter(m => m.atrasada && m.statusMeta !== 'concluida')
    : metas;

  const tabList = [
    { id: 'todas',     label: `Todas (${metas.length})` },
    { id: 'ativa',     label: `Em andamento (${metas.filter(m => m.statusMeta === 'ativa' && !m.atrasada).length})` },
    { id: 'atrasada',  label: `Atrasadas (${metas.filter(m => m.atrasada && m.statusMeta !== 'concluida').length})` },
    { id: 'concluida', label: `Concluídas (${metas.filter(m => m.statusMeta === 'concluida' || m.percentual >= 100).length})` },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Metas Financeiras
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Defina objetivos por período e acompanhe seu progresso em tempo real.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={load} className="fin-btn-secondary text-xs">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { setEditItem(null); setModalOpen(true); }}
            className="fin-btn-primary text-xs flex items-center gap-1.5"
            style={{ background: accent }}
          >
            <Plus size={14} /> Nova Meta
          </button>
        </div>
      </div>

      {/* Mini dashboard */}
      {metas.length > 0 && <SummaryDashboard metas={metas} accent={accent} />}

      {/* Overdue alert */}
      {metas.filter(m => m.atrasada && m.statusMeta !== 'concluida').length > 0 && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
          <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 font-medium">
            Você tem <strong>{metas.filter(m => m.atrasada && m.statusMeta !== 'concluida').length}</strong> meta{metas.filter(m => m.atrasada).length !== 1 ? 's' : ''} com prazo vencido. Revise ou atualize o prazo.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        {tabList.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-shrink-0 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              tab === t.id ? 'border-current' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
            style={tab === t.id ? { color: accent, borderColor: accent } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="p-10 text-center text-slate-400 text-sm">Carregando...</div>
      ) : displayed.length === 0 ? (
        <div className="fin-card p-12 text-center">
          <Target size={36} className="mx-auto mb-3 text-slate-200" />
          <p className="font-semibold text-slate-600 dark:text-slate-300">
            {tab === 'todas' ? 'Nenhuma meta criada ainda' : 'Nenhuma meta nesta categoria'}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            {tab === 'todas' && 'Crie sua primeira meta financeira para acompanhar seu progresso.'}
          </p>
          {tab === 'todas' && (
            <button
              onClick={() => { setEditItem(null); setModalOpen(true); }}
              className="mt-4 fin-btn-primary text-sm"
              style={{ background: accent }}
            >
              Criar primeira meta
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayed.map(m => (
            <MetaCard
              key={m.id}
              meta={m}
              accent={accent}
              onEdit={item => { setEditItem(item); setModalOpen(true); }}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
            />
          ))}
        </div>
      )}

      <MetaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        editItem={editItem}
        accent={accent}
      />
    </div>
  );
}
